/**
 * One payment method in the "Payment methods" section of the property and
 * apartment edit sheets.
 *
 * The two sheets need different controls — a property states a value, so it gets
 * a switch; an apartment may defer to its property, so it gets an Inherit / On /
 * Off select — but the row around the control is the same, and so is the way a
 * locked or inherited state is explained. Keeping it here means the two sections
 * cannot drift into looking like different features.
 */
import type { ReactNode } from "react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

type RowProps = {
  label: string;
  description: string;
  /** Whether the method ends up ON — drives the muted treatment. */
  enabled: boolean;
  /** Short note under the label: why it is locked, or where it came from. */
  note?: ReactNode;
  /** The control on the right. */
  children: ReactNode;
};

export function PaymentMethodRow({
  label,
  description,
  enabled,
  note,
  children,
}: RowProps) {
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-lg border px-3 py-3 ${
        enabled ? "" : "bg-muted/40"
      }`}
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium">{label}</span>
          {note}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex-none">{children}</div>
    </div>
  );
}

type SwitchRowProps = Omit<RowProps, "children" | "enabled"> & {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onCheckedChange: (checked: boolean) => void;
};

/** Property-level row: the property states a value, so a plain switch. */
export function PaymentMethodSwitchRow({
  id,
  label,
  description,
  note,
  checked,
  disabled,
  onCheckedChange,
}: SwitchRowProps) {
  return (
    <PaymentMethodRow
      label={label}
      description={description}
      enabled={checked}
      note={note}
    >
      {/* The label is the accessible name for the switch, so screen readers
          announce which method is being toggled rather than just "switch". */}
      <Label htmlFor={id} className="sr-only">
        {label}
      </Label>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </PaymentMethodRow>
  );
}
